import { kolkataToday, addDays } from './previousPeriod';

export const DEFAULT_DATE_PRESET = 'last_30_days';
export const REPORT_DEFAULT_PRESET = 'report_default';
export const CUSTOM_DATE_PRESET = 'custom';

export const DATE_PRESET_IDS = Object.freeze([
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
  'last_90_days',
  'this_month',
  'all_time',
]);

/** Number reports follow the workspace KPI widget: all time, today, this week. */
export const NUMBER_DATE_PRESET_IDS = Object.freeze([
  'all_time',
  'today',
  'this_week',
]);

export function dateRangePayload(dateRange, today = kolkataToday()) {
  if (dateRange?.preset === CUSTOM_DATE_PRESET) {
    return {
      start: dateRange.start || undefined,
      end: dateRange.end || undefined,
    };
  }
  if (dateRange?.preset === 'today') {
    const d = dateRange.start || today;
    return {
      start: d,
      end: dateRange.end || d,
    };
  }
  if (dateRange?.preset === 'yesterday') {
    const yDay = addDays(today, -1);
    const d = dateRange.start || yDay;
    return {
      start: d,
      end: dateRange.end || d,
    };
  }
  const known = DATE_PRESET_IDS.includes(dateRange?.preset)
    || NUMBER_DATE_PRESET_IDS.includes(dateRange?.preset);
  const preset = known ? dateRange.preset : DEFAULT_DATE_PRESET;
  return { preset };
}

export function dateRangeFromSpec(dateRange, today = kolkataToday()) {
  const preset = dateRange?.preset;
  if (DATE_PRESET_IDS.includes(preset) || NUMBER_DATE_PRESET_IDS.includes(preset)) {
    return { preset };
  }
  if (dateRange?.start && dateRange?.end && dateRange.start === dateRange.end) {
    if (dateRange.start === today) return { preset: 'today' };
    if (dateRange.start === addDays(today, -1)) return { preset: 'yesterday' };
  }
  if (dateRange?.start || dateRange?.end) {
    return {
      preset: CUSTOM_DATE_PRESET,
      start: dateRange.start || '',
      end: dateRange.end || '',
    };
  }
  return { preset: 'all_time' };
}

export function applyDateOverride(querySpec, view, today = kolkataToday()) {
  if (!view || view.preset === REPORT_DEFAULT_PRESET) {
    return querySpec;
  }
  if (view.preset === CUSTOM_DATE_PRESET) {
    return {
      ...querySpec,
      date_range: {
        start: view.start || undefined,
        end: view.end || undefined,
      },
    };
  }
  if (view.preset === 'today') {
    const d = view.start || today;
    return {
      ...querySpec,
      date_range: {
        start: d,
        end: view.end || d,
      },
    };
  }
  if (view.preset === 'yesterday') {
    const yDay = addDays(today, -1);
    const d = view.start || yDay;
    return {
      ...querySpec,
      date_range: {
        start: d,
        end: view.end || d,
      },
    };
  }
  return {
    ...querySpec,
    date_range: { preset: view.preset },
  };
}

export function datePresetLabels(copy) {
  return {
    today: copy.datePresetToday,
    yesterday: copy.datePresetYesterday,
    last_7_days: copy.datePresetLast7,
    last_30_days: copy.datePresetLast30,
    last_90_days: copy.datePresetLast90,
    this_month: copy.datePresetThisMonth,
    this_week: copy.datePresetThisWeek,
    all_time: copy.datePresetAllTime,
    custom: copy.datePresetCustom,
    report_default: copy.datePresetReportDefault,
  };
}

export function savedRangeLabel(dateRange, labels, today = kolkataToday()) {
  if (DATE_PRESET_IDS.includes(dateRange?.preset) || NUMBER_DATE_PRESET_IDS.includes(dateRange?.preset)) {
    return labels[dateRange.preset] || dateRange.preset;
  }
  const start = dateRange?.start || '';
  const end = dateRange?.end || '';
  if (start && end) {
    if (start === end) {
      if (start === today) return labels.today || 'Today';
      if (start === addDays(today, -1)) return labels.yesterday || 'Yesterday';
    }
    return `${start} – ${end}`;
  }
  if (start || end) return start || end;
  return labels.all_time;
}

export function activeRangeLabel(dateRange, view, labels) {
  if (!view || view.preset === REPORT_DEFAULT_PRESET) {
    return savedRangeLabel(dateRange, labels);
  }
  if (view.preset === CUSTOM_DATE_PRESET) {
    const start = view.start || '';
    const end = view.end || '';
    if (start && end) return `${start} – ${end}`;
    return labels.custom;
  }
  return labels[view.preset] || view.preset;
}
